/**
 * In-app confirmation — the platform's replacement for `window.confirm`.
 *
 * Never use the native dialog for a confirmation: a browser can suppress it
 * ("prevent this page from creating additional dialogs", some embedded and
 * sandboxed contexts), and then `confirm()` returns `false` at once with no
 * trace — the button the user clicked silently does nothing. That is how the
 * guild invite "revoke" button looked broken on staging (2026-10-27).
 *
 * Usage, from any component under <ConfirmDialogProvider /> (mounted once at
 * the root in main.tsx, beside PanelHost):
 *
 *   const confirm = useConfirm();
 *   const handleDelete = async () => {
 *     if (!(await confirm({ title: 'Delete this trip?', destructive: true }))) return;
 *     await deleteTrip(id);
 *   };
 *
 * `confirm` resolves `true` only when the user presses the confirm button.
 * Cancel, Escape, clicking outside, a second request arriving, and the
 * provider unmounting all resolve `false` — a confirmation fails closed.
 *
 * The architecture test `test/architecture/no-native-confirm.test.ts` keeps
 * `window.confirm` / bare `confirm(` out of client source.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface ConfirmOptions {
  /** The question, as a short sentence ending in "?". */
  title: string;
  /** What happens if they go ahead — consequences, what is kept, whether it can be undone. */
  description?: ReactNode;
  /** Label of the confirming button. Default "Continue". Name the action ("Delete trip"). */
  confirmLabel?: string;
  /** Label of the cancel button. Default "Cancel". */
  cancelLabel?: string;
  /** Styles the confirming button as destructive. */
  destructive?: boolean;
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
  /** The element focused when the question was asked — usually the button. */
  returnFocusTo: HTMLElement | null;
}

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  // The latest pending request, readable from callbacks and the unmount effect
  // without stale closures. `confirm` and `settle` write it directly; the
  // effect only re-syncs it after a render.
  const pendingRef = useRef<PendingConfirm | null>(null);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  // Where keyboard focus goes back to when the dialog closes. There is no
  // AlertDialogTrigger (the question is asked imperatively), so Radix has no
  // trigger to return to; we restore the asking element ourselves.
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    returnFocusRef.current = current.returnFocusTo;
    setPending(null);
    current.resolve(confirmed);
  }, []);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // A request that arrives while another is open supersedes it; the
        // earlier caller gets a "no", never a hang.
        pendingRef.current?.resolve(false);
        const active = typeof document !== 'undefined' ? document.activeElement : null;
        const next: PendingConfirm = {
          options,
          resolve,
          returnFocusTo: active instanceof HTMLElement ? active : null,
        };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  // Unmounting with a question open answers it "no" so no caller awaits forever.
  useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    [],
  );

  const value = useMemo(() => confirm, [confirm]);
  const options = pending?.options;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            const target = returnFocusRef.current;
            returnFocusRef.current = null;
            if (target && target.isConnected) {
              event.preventDefault();
              target.focus();
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title}</AlertDialogTitle>
            {options?.description !== undefined && options.description !== null ? (
              <AlertDialogDescription className="whitespace-pre-line">
                {options.description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>
              {options?.cancelLabel ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                settle(true);
              }}
              className={
                options?.destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              {options?.confirmLabel ?? 'Continue'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/**
 * The in-app `confirm`. Throws outside <ConfirmDialogProvider /> rather than
 * falling back to the native dialog — the fallback would be the bug.
 */
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within <ConfirmDialogProvider>');
  }
  return confirm;
}
